import { BaseApiService } from './baseApiService';
import { ApiSuccessResponse } from '../../types/api';

// ── Response shapes (authoritative backend contract) ──────────────
//
// Session-usage endpoints live on the EHR backend (same base URL as the
// AI-usage / `usageService` dashboard). They surface the per-doctor monthly
// session cap and a super-admin roll-up by clinic + doctor.

/**
 * GET /usage/session-usage/me/ → data
 *
 * The current doctor's session usage for the active billing period.
 * `limit`/`remaining` are `null` when the plan grants unlimited sessions.
 */
export interface MySessionUsage {
  period: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  plan_slug: string | null;
}

/** A single doctor row inside a clinic's session-usage report. */
export interface SessionUsageDoctor {
  doctor_id: number;
  name: string;
  used: number;
  limit: number | null;
  pct: number;
}

/** A clinic's aggregate session usage plus its per-doctor breakdown. */
export interface SessionUsageClinic {
  clinic_id: number;
  used: number;
  allocated: number;
  doctors: SessionUsageDoctor[];
}

/**
 * GET /usage/session-usage/report/ → data
 *
 * Super-admin roll-up of session usage across clinics for a period.
 */
export interface SessionUsageReport {
  period: string;
  clinic_filter: number | null;
  clinics: SessionUsageClinic[];
}

export interface SessionUsageReportParams {
  /** Billing period as 'YYYY-MM'. Defaults (server-side) to the current month. */
  period?: string;
  /** Narrow the report to a single clinic. */
  clinic_id?: number;
}

// ── Service ───────────────────────────────────────────────────────

class SessionUsageService extends BaseApiService {
  /** The current doctor's session usage for the active period. */
  async getMySessionUsage(): Promise<MySessionUsage> {
    const response = await this.fetchWithAuth<ApiSuccessResponse<MySessionUsage>>(
      '/usage/session-usage/me/',
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    if (response.status !== 'success') {
      throw new Error(`API error: ${response.message}`);
    }
    return response.data;
  }

  /** Super-admin session-usage roll-up by clinic and doctor. */
  async getSessionUsageReport(
    params: SessionUsageReportParams = {}
  ): Promise<SessionUsageReport> {
    const query = new URLSearchParams();

    if (params.period) {
      query.append('period', params.period);
    }
    if (params.clinic_id !== undefined && params.clinic_id !== null) {
      query.append('clinic_id', String(params.clinic_id));
    }

    const queryString = query.toString();
    const endpoint = queryString
      ? `/usage/session-usage/report/?${queryString}`
      : '/usage/session-usage/report/';

    const response = await this.fetchWithAuth<ApiSuccessResponse<SessionUsageReport>>(
      endpoint,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    if (response.status !== 'success') {
      throw new Error(`API error: ${response.message}`);
    }
    return response.data;
  }
}

export const sessionUsageService = new SessionUsageService();
